import { formatInTimeZone } from 'date-fns-tz';
import { resolveDayOf } from 'src/pages/Map/displayDays';

import { Lane } from './types';

/**
 * ISO → 業務日 'YYYY-MM-DD'（JST 固定）。displayDays.ts の toBusinessDayString と同じ規則。
 *
 * 公開しているのは、バーの「期間より前に積む」判定（LongHaulBoard）が同じ規則を要るため。
 * 3 つ目の写しを作るとブラウザ TZ の扱いが画面の中で食い違う。
 */
export const businessDayOf = (iso: string): string | null => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : formatInTimeZone(date, 'Asia/Tokyo', 'yyyy-MM-dd');
};

export type DropTarget = {
  droppableId: string;
  shiftTruckId: number;
  targetRowId: string;
  startDayIndex: number;
  endDayIndex: number;
  /** 勤務の開始・終了（完全な ISO）。日カラムの中で実際に働く時間帯を描くのに使う */
  startAt: string;
  endAt: string;
};

export const WORK_DROPPABLE_PREFIX = 'lh-work::';

/**
 * 勤務が覆う日の範囲を 1 つのドロップ先にする。
 *
 * Why not 日セルごと: 保存経路は shift_truck_id / order_id / action / cycle しか
 * 運ばず、日はサーバーへの入力にならない。複数日勤務で日セルを分けると、
 * どの日に落としても同じ結果になる操作を画面へ出すことになる。
 */
export function buildDropTargets(lane: Lane, days: readonly string[]): DropTarget[] {
  if (days.length === 0) return [];

  const targets: DropTarget[] = [];
  for (const work of lane.workTargets) {
    const startDay = resolveDayOf(work.startAt, days);
    const endDay = resolveDayOf(work.endAt, days);
    if (!startDay || !endDay) continue;

    // resolveDayOf は期間外を端へ寄せるので、寄せる前の業務日で「期間と重なるか」を見る。
    // Why not work.startAt.slice(0, 10): ISO のローカル日付部分は JST とは限らない。
    // 業務日の判定は 1 本に揃える（displayDays.ts の resolveDayOf と同じ規則）
    const startBusinessDay = businessDayOf(work.startAt);
    const endBusinessDay = businessDayOf(work.endAt);
    if (!startBusinessDay || !endBusinessDay) continue;
    if (endBusinessDay < days[0] || startBusinessDay > days[days.length - 1]) continue;

    const startDayIndex = days.indexOf(startDay);
    const endDayIndex = days.indexOf(endDay);
    if (startDayIndex < 0 || endDayIndex < 0) continue;

    targets.push({
      droppableId: `${WORK_DROPPABLE_PREFIX}${work.shiftTruckId}`,
      shiftTruckId: work.shiftTruckId,
      targetRowId: work.targetRowId,
      startDayIndex,
      endDayIndex,
      startAt: work.startAt,
      endAt: work.endAt,
    });
  }
  return targets;
}
